import {
  ChatWrapper,
  ChatItemWrapper,
  ChatItemIcon,
  ChatItemContent,
  ChatItemHeader,
  ChatItemSender,
  ChatItemDate,
  ChatItemMessage,
  ChatBody,
  ChatInputWrapper,
  ChatInputContainer,
  ChatInputDisclaimer,
  StyledInput,
  StreamingCursor,
  ScrollToBottomButton,
  DocumentMessageWrapper,
  DocumentMessageIconWrapper,
  DocumentMessageName,
  DocumentDownloadButton,
  ChatItemActions,
  ChatItemActionButton,
  DateSeparator,
  DateSeparatorText,
} from "./Styles";
import { ThinkingWrapper, ThinkingRing, ThinkingText } from "../ProcessingMessage";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { FormattedText } from "../FormattedText";
import { formatTextToHtml } from "../FormattedText/toHtml";
import UserIcon from "../../Assets/User-icon.png";
import { FiCopy, FiCheck, FiArrowUp, FiDownload, FiPaperclip } from "react-icons/fi";
import { AiOutlineLike, AiOutlineDislike } from "react-icons/ai";
import { Button } from "../Button";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Chat as ChatDTO, Language } from "../../Common/Types-DTOs/Chat";
import {
  Message,
  MessageDTOType,
  MessageSenderType as Sender,
} from "../../Common/Types-DTOs/Message";
import { HiOutlineDocumentText } from "react-icons/hi";
import { eventBus } from "../../Common/EventBus";
import { Notification } from "../Notifications";
import { formatDateLabel, getDateKey } from "../../Common/Utils/dateUtils";
import { combineAttachedFiles } from "../../Common/Utils/documentAttachments";

// Single point of change for the sender label so it's easy to localize later.
function getSenderLabel(sender: string, t: TFunction): string {
  return sender === Sender.BOT
    ? t("chat.chatComponent.senderBot")
    : t("chat.chatComponent.senderUser");
}

// A DOCUMENT message and the TEXT message the user/bot sent right alongside it are two
// separate backend messages, but rendered (and exported) as a single bubble: the TEXT is
// folded into the DOCUMENT's pairedText, so it must be skipped where it'd otherwise appear on its own.
function getDocumentPairing(
  message: Message,
  prev: Message | undefined,
  next: Message | undefined
): { skip: boolean; pairedText?: string } {
  if (
    message.messageType === MessageDTOType.TEXT &&
    message.sender === Sender.USER &&
    prev?.messageType === MessageDTOType.DOCUMENT &&
    prev.sender === Sender.USER
  ) {
    return { skip: true };
  }
  if (
    message.messageType === MessageDTOType.TEXT &&
    message.sender === Sender.BOT &&
    next?.messageType === MessageDTOType.DOCUMENT &&
    next.sender === Sender.BOT
  ) {
    return { skip: true };
  }
  if (message.messageType === MessageDTOType.DOCUMENT) {
    if (
      message.sender === Sender.USER &&
      next?.messageType === MessageDTOType.TEXT &&
      next.sender === Sender.USER
    ) {
      return { skip: false, pairedText: next.text ?? "" };
    }
    if (
      message.sender === Sender.BOT &&
      prev?.messageType === MessageDTOType.TEXT &&
      prev.sender === Sender.BOT
    ) {
      return { skip: false, pairedText: prev.text ?? "" };
    }
  }
  return { skip: false };
}

export const Chat = ({
  chat,
  disabledInput = false,
  sendMessage,
  sendDocument,
  onMessageFeedback,
  streamingText = null,
  thinkingText = null,
  streamingBotMessageId = null,
}: {
  chat: ChatDTO;
  disabledInput?: boolean;
  sendMessage?: (message: string) => Promise<void>;
  sendDocument?: (files: File[], text?: string) => Promise<void>;
  onMessageFeedback?: (messageId: string, feedback: 1 | -1 | 0) => Promise<void>;
  streamingText?: string | null;
  thinkingText?: string | null;
  streamingBotMessageId?: string | null;
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [pendingDocuments, setPendingDocuments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const isSubmittingRef = useRef(false);
  const chatBodyRef = useRef<HTMLDivElement | null>(null);
  const endOfMessagesRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const prevStreamingNullRef = useRef(true);

  const getFileTypeInfo = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return { label: 'PDF', bg: '#fee2e2', fg: '#dc2626' };
    if (ext === 'docx' || ext === 'doc') return { label: 'DOC', bg: '#dbeafe', fg: '#2563eb' };
    return { label: 'TXT', bg: '#f3f4f6', fg: '#6b7280' };
  };

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (files.length === 0) return;

    setPendingDocuments((prev) => combineAttachedFiles(files, prev));
  }, []);

  const handleScroll = useCallback(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsAtBottom(distFromBottom < 80);
  }, []);

  // Auto-scroll only when user is already at the bottom
  useEffect(() => {
    if (!isAtBottom) return;
    const el = chatBodyRef.current;
    const end = endOfMessagesRef.current;
    if (el && end) {
      el.scrollTo({ top: end.offsetTop - el.offsetTop, behavior: 'instant' as ScrollBehavior });
    }
  }, [chat.messages, streamingText, isAtBottom]);

  // When streaming begins (null → non-null), scroll to bottom unconditionally
  useEffect(() => {
    const wasNull = prevStreamingNullRef.current;
    prevStreamingNullRef.current = streamingText === null;
    if (wasNull && streamingText !== null) {
      const el = chatBodyRef.current;
      const end = endOfMessagesRef.current;
      if (el && end) {
        el.scrollTo({ top: end.offsetTop - el.offsetTop, behavior: 'instant' as ScrollBehavior });
      }
      setIsAtBottom(true);
    }
  }, [streamingText]);

  const scrollToBottom = useCallback(() => {
    const el = chatBodyRef.current;
    const end = endOfMessagesRef.current;
    if (el && end) {
      el.scrollTo({ top: end.offsetTop - el.offsetTop, behavior: 'smooth' });
    }
    setIsAtBottom(true);
  }, []);

  const isLoading = useMemo(() => {
    const length = chat.messages.length;
    if (length === 0) {
      return false;
    }
    const lastMessage = chat.messages[length - 1];

    return lastMessage.sender == Sender.USER;
  }, [chat.messages]);

  const getChatTitle = useMemo(() => {
    if (chat.messages && chat.messages.length > 0) {
      const firstMessage = chat.messages[0].text || "";
      return firstMessage.length > 50 ? firstMessage.substring(0, 50) + "..." : firstMessage;
    }
    return t("chat.chatComponent.untitledChat");
  }, [chat.messages, t]);

  // Group messages by date
  const groupedMessages = useMemo(() => {
    const groups: { date: Date; dateLabel: string; messages: Message[] }[] = [];

    // While streamingText is active, hide the bot message that's being streamed
    // so only the streaming bubble shows (no duplicate, no flicker on done).
    const messagesToShow =
      streamingText !== null && streamingBotMessageId
        ? chat.messages.filter((m) => m.id !== streamingBotMessageId)
        : chat.messages;

    messagesToShow.forEach((message) => {
      const dateKey = getDateKey(message.creationDate);
      const existingGroup = groups.find(g => getDateKey(g.date) === dateKey);

      if (existingGroup) {
        existingGroup.messages.push(message);
      } else {
        groups.push({
          date: message.creationDate,
          dateLabel: formatDateLabel(message.creationDate),
          messages: [message],
        });
      }
    });

    return groups;
  }, [chat.messages, streamingText, streamingBotMessageId]);

  // Escape HTML entities to prevent XSS attacks
  const escapeHtml = (text: string | undefined): string => {
    if (!text) return "";
    const map: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#x27;",
    };
    return text.replace(/[&<>"']/g, (char) => map[char] || char);
  };

  const handleExportToPDF = () => {
    // Crear contenido HTML para el PDF
    const messages = chat.messages;
    const chatContent = messages
      .map((message, index) => {
        const prev = messages[index - 1];
        const next = messages[index + 1];
        const { skip, pairedText } = getDocumentPairing(message, prev, next);
        if (skip) {
          return null;
        }

        const senderLabel = getSenderLabel(message.sender, t);
        const date = new Date(message.creationDate).toLocaleString("es-ES");

        let bodyHtml: string;
        if (message.messageType === MessageDTOType.DOCUMENT) {
          const documentLine = `📎 ${escapeHtml(message.documentName)}`;
          bodyHtml = pairedText ? `${formatTextToHtml(pairedText)}<br/>${documentLine}` : documentLine;
        } else {
          bodyHtml = formatTextToHtml(message.text);
        }

        return `<div style="margin-bottom: 1rem; white-space: pre-line;"><strong>${senderLabel}</strong> (${date}):<br/>${bodyHtml}</div>`;
      })
      .filter((html): html is string => html !== null)
      .join("");

    const escapedTitle = escapeHtml(getChatTitle);

    const htmlContent = `
      <!DOCTYPE html>
      <html lang="es-ES">
        <head>
          <meta charset="UTF-8">
          <title>${t("chat.chatComponent.exportTitlePrefix", { title: escapedTitle })}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 18px; margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <h1>${escapedTitle}</h1>
          ${chatContent}
        </body>
      </html>
    `;

    // Crear blob y descargar
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `chat-${chat.id || "export"}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Intentar abrir en nueva ventana para imprimir como PDF
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      // Call print directly after closing the document instead of relying on onload
      // The onload event may not fire reliably when the window is already loaded
      setTimeout(() => {
        printWindow.print();
      }, 100);
    }
  };

  return (
    <ChatWrapper>
      <button
        onClick={handleExportToPDF}
        title={t("chat.chatComponent.exportButtonTitle")}
        style={{
          position: 'absolute',
          top: '1.5rem',
          right: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.5rem',
          backgroundColor: 'transparent',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          color: '#111827',
          cursor: 'pointer',
          transition: 'all 0.2s',
          width: '2.5rem',
          height: '2.5rem',
          zIndex: 100,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#F9FAFB';
          e.currentTarget.style.borderColor = '#D1D5DB';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.borderColor = '#E5E7EB';
        }}
      >
        <FiDownload size={18} />
      </button>
      <ChatBody ref={chatBodyRef} onScroll={handleScroll}>
        {groupedMessages.map((group, groupIndex) => (
          <div key={`group-${groupIndex}`} style={{ width: '100%' }}>
            <DateSeparator>
              <DateSeparatorText>{group.dateLabel}</DateSeparatorText>
            </DateSeparator>
            {group.messages.map((message, msgIndex) => {
              const prev = group.messages[msgIndex - 1];
              const next = group.messages[msgIndex + 1];
              const { skip, pairedText } = getDocumentPairing(message, prev, next);
              if (skip) {
                return null;
              }
              return (
                <ChatItem
                  language={chat.language ?? Language.SPANISH}
                  message={message}
                  pairedText={pairedText}
                  chatId={chat.id}
                  onMessageFeedback={onMessageFeedback}
                  key={`msg-${groupIndex}-${msgIndex}`}
                />
              );
            })}
          </div>
        ))}
        {streamingText !== null ? (
          <ChatItem
            language={chat.language ?? Language.SPANISH}
            message={{
              id: "streaming",
              messageType: MessageDTOType.TEXT,
              text: streamingText,
              sender: Sender.BOT,
              creationDate: new Date(),
            }}
            showCursor={true}
            thinkingText={thinkingText}
          />
        ) : isLoading ? (
          thinkingText ? (
            <ChatItem
              language={chat.language ?? Language.SPANISH}
              message={{
                id: "thinking",
                messageType: MessageDTOType.TEXT,
                text: thinkingText,
                sender: Sender.BOT,
                creationDate: new Date(),
              }}
              isThinking={true}
            />
          ) : (
            <ChatItem
              language={chat.language ?? Language.SPANISH}
              message={{
                id: "",
                messageType: MessageDTOType.TEXT,
                text: "",
                sender: Sender.BOT,
                creationDate: new Date(),
              }}
              isLoading={true}
            />
          )
        ) : null}
        <div ref={endOfMessagesRef} />
      </ChatBody>
      {streamingText !== null && !isAtBottom && (
        <ScrollToBottomButton onClick={scrollToBottom} title={t("chat.chatComponent.scrollToBottomTitle")}>
          ↓
        </ScrollToBottomButton>
      )}
      {!disabledInput && (
        <ChatInputWrapper>
          <ChatInputContainer>
            {sendDocument && (
              <>
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  style={{ display: "none" }}
                />
                <Button
                  onClick={handleAttachClick}
                  disabled={isLoading || isSubmitting}
                  style={{ marginRight: "4px" }}
                  title={t("chat.chatComponent.attachButtonTitle")}
                >
                  <FiPaperclip size={18} />
                </Button>
              </>
            )}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.375rem", minWidth: 0 }}>
              {pendingDocuments.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
                  {pendingDocuments.map((doc, index) => {
                    const ft = getFileTypeInfo(doc.name);
                    return (
                      <div key={`${doc.name}-${index}`} style={{
                        display: "inline-flex", alignItems: "center", gap: "0.5rem",
                        padding: "0.375rem 0.5rem 0.375rem 0.375rem",
                        borderRadius: "8px",
                        backgroundColor: "#ffffff", border: "1px solid #e5e7eb",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        fontSize: "0.75rem", color: "#374151", maxWidth: "260px",
                      }}>
                        <div style={{
                          display: "flex", alignItems: "center", justifyContent: "center",
                          width: "2rem", height: "2rem", borderRadius: "6px",
                          backgroundColor: ft.bg, color: ft.fg,
                          fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.04em", flexShrink: 0,
                        }}>
                          {ft.label}
                        </div>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, fontWeight: 500 }}>
                          {doc.name}
                        </span>
                        <button
                          onClick={() => setPendingDocuments((prev) => prev.filter((_, i) => i !== index))}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: "#9ca3af", flexShrink: 0, lineHeight: 1, borderRadius: "4px", display: "flex", alignItems: "center" }}
                          title={t("chat.chatComponent.removeDocumentTitle")}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <StyledInput
                placeholder={
                  isLoading
                    ? t("chat.chatComponent.placeholderThinking")
                    : pendingDocuments.length > 0
                      ? t("chat.chatComponent.placeholderDocumentInstructions")
                      : t("chat.chatComponent.placeholderDefault")
                }
                onChange={(e) => setInputValue(e.target.value)}
                value={inputValue}
                disabled={isLoading}
                onKeyDown={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key === "Enter" && !e.shiftKey && (inputValue || pendingDocuments.length > 0) && !isLoading && !isSubmittingRef.current) {
                    e.preventDefault();
                    isSubmittingRef.current = true;
                    setIsSubmitting(true);
                    try {
                      if (pendingDocuments.length > 0 && sendDocument) {
                        await sendDocument(pendingDocuments, inputValue || undefined);
                        setPendingDocuments([]);
                      } else {
                        sendMessage && (await sendMessage(inputValue));
                      }
                      setInputValue("");
                    } finally {
                      isSubmittingRef.current = false;
                      setIsSubmitting(false);
                    }
                  }
                }}
              />
            </div>
            <Button
              onClick={async () => {
                if (isLoading || isSubmittingRef.current || (!inputValue && pendingDocuments.length === 0)) return;
                isSubmittingRef.current = true;
                setIsSubmitting(true);
                try {
                  if (pendingDocuments.length > 0 && sendDocument) {
                    await sendDocument(pendingDocuments, inputValue || undefined);
                    setPendingDocuments([]);
                  } else {
                    sendMessage && (await sendMessage(inputValue));
                  }
                  setInputValue("");
                } finally {
                  isSubmittingRef.current = false;
                  setIsSubmitting(false);
                }
              }}
              disabled={isLoading || isSubmitting || (!inputValue && pendingDocuments.length === 0)}
            >
              <FiArrowUp size={18} />
            </Button>
          </ChatInputContainer>
          <ChatInputDisclaimer>
            {t("chat.chatComponent.disclaimer")}
          </ChatInputDisclaimer>
        </ChatInputWrapper>
      )}
    </ChatWrapper>
  );
};

export const ChatItem = ({
  language,
  message,
  isLoading,
  isThinking,
  pairedText,
  showCursor,
  thinkingText,
  chatId,
  onMessageFeedback,
}: {
  language: Language;
  message: Message;
  isLoading?: boolean;
  isThinking?: boolean;
  pairedText?: string;
  showCursor?: boolean;
  thinkingText?: string | null;
  chatId?: string;
  onMessageFeedback?: (messageId: string, feedback: 1 | -1 | 0) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const [feedback, setFeedback] = useState<1 | -1 | 0 | null>(message.feedback ?? null);
  const [justVotedUp, setJustVotedUp] = useState(false);
  const [justVotedDown, setJustVotedDown] = useState(false);

  useEffect(() => {
    setFeedback(message.feedback ?? null);
  }, [message.id]);

  const sender = useMemo(() => {
    return message.sender == Sender.BOT ? Sender.BOT : Sender.USER;
  }, [message.sender]);

  const displayedSender = useMemo(() => getSenderLabel(sender, t), [sender, t]);

  const messageText = useMemo(() => {
    if (message.messageType === MessageDTOType.TEXT) {
      return message.text;
    }
    return "";
  }, [message, language]);

  const handleCopy = async () => {
    if (!messageText) return;
    try {
      await navigator.clipboard.writeText(messageText);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1000);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("success", t("chat.chatComponent.notifications.copiedSuccess"))
      );
    } catch (error) {
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("chat.chatComponent.notifications.copyError"))
      );
    }
  };

  const handleVote = async (vote: 1 | -1) => {
    if (!chatId || !message.id || !onMessageFeedback) return;

    const previousFeedback = feedback;
    const nextFeedback: 1 | -1 | 0 = feedback === vote ? 0 : vote;
    setFeedback(nextFeedback);

    if (vote === 1) {
      setJustVotedUp(true);
      setTimeout(() => setJustVotedUp(false), 1000);
    } else {
      setJustVotedDown(true);
      setTimeout(() => setJustVotedDown(false), 1000);
    }

    try {
      await onMessageFeedback(message.id, nextFeedback);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("success", t("chat.chatComponent.notifications.feedbackThanks"))
      );
    } catch (error) {
      setFeedback(previousFeedback);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("chat.chatComponent.notifications.feedbackError"))
      );
    }
  };

  return (
    <ChatItemWrapper $sender={sender}>
      {sender === Sender.BOT && (
        <ChatItemIcon>
          <img src="/mike_logo.png" alt={t("chat.chatComponent.mikeLogoAlt")} />
        </ChatItemIcon>
      )}
      <ChatItemContent $sender={sender}>
        <ChatItemHeader $sender={sender}>
          <ChatItemSender>{displayedSender}</ChatItemSender>
        </ChatItemHeader>
        <ChatItemMessage $sender={sender}>
          {isLoading ? (
            <ThinkingWrapper>
              <ThinkingRing />
            </ThinkingWrapper>
          ) : isThinking ? (
            <ThinkingWrapper>
              <ThinkingRing />
              <ThinkingText>
                {message.messageType === MessageDTOType.TEXT
                  ? message.text ?? ""
                  : ""}
              </ThinkingText>
            </ThinkingWrapper>
          ) : (
            <>
              {MessageDTOType.TEXT == message.messageType && (
                <>
                  <FormattedText text={messageText ?? ""} />
                  {showCursor && !thinkingText && <StreamingCursor />}
                </>
              )}
              {MessageDTOType.DOCUMENT == message.messageType && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {pairedText && <FormattedText text={pairedText} />}
                  <DocumentMessageWrapper>
                    <DocumentMessageIconWrapper>
                      <HiOutlineDocumentText />
                    </DocumentMessageIconWrapper>
                    <DocumentMessageName>
                      {message.documentName}
                    </DocumentMessageName>
                    {message.documentUrl && (
                      <DocumentDownloadButton
                        onClick={() => window.open(message.documentUrl, "_blank")}
                      >
                        <FiDownload />
                      </DocumentDownloadButton>
                    )}
                  </DocumentMessageWrapper>
                </div>
              )}
            </>
          )}
        </ChatItemMessage>
        {!isLoading && !isThinking && sender === Sender.BOT && (
          showCursor ? (
            thinkingText ? (
              <ThinkingWrapper style={{ marginTop: '0.375rem' }}>
                <ThinkingRing />
                <ThinkingText>{thinkingText}</ThinkingText>
              </ThinkingWrapper>
            ) : null
          ) : (
            <ChatItemActions $sender={sender}>
              {message.messageType === MessageDTOType.TEXT && (
                <ChatItemActionButton onClick={handleCopy} title={t("chat.chatComponent.copyButtonTitle")}>
                  {isCopied ? <FiCheck size={18} style={{ color: '#22c55e' }} /> : <FiCopy size={18} />}
                </ChatItemActionButton>
              )}
              <ChatItemActionButton onClick={() => handleVote(1)} title={t("chat.chatComponent.likeButtonTitle")}>
                {justVotedUp ? (
                  <FiCheck size={18} style={{ color: '#22c55e' }} />
                ) : (
                  <AiOutlineLike size={18} style={feedback === 1 ? { color: '#000000' } : undefined} />
                )}
              </ChatItemActionButton>
              <ChatItemActionButton onClick={() => handleVote(-1)} title={t("chat.chatComponent.dislikeButtonTitle")}>
                {justVotedDown ? (
                  <FiCheck size={18} style={{ color: '#22c55e' }} />
                ) : (
                  <AiOutlineDislike size={18} style={feedback === -1 ? { color: '#000000' } : undefined} />
                )}
              </ChatItemActionButton>
            </ChatItemActions>
          )
        )}
      </ChatItemContent>
      {sender === Sender.USER && (
        <ChatItemIcon>
          <img src={UserIcon} alt={t("chat.chatComponent.userIconAlt")} />
        </ChatItemIcon>
      )}
      <ChatItemDate $sender={sender}>
        {`${message.creationDate.getHours()}:${message.creationDate.getMinutes().toString().padStart(2, "0")}`}
      </ChatItemDate>
    </ChatItemWrapper>
  );
};
